import { createElement, SyntheticEvent, useEffect, useState } from "react";
import { Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, IconButton, List, ListItem, ListItemText, Modal, Typography } from "@mui/material";
import Grid from "@mui/system/Grid";
import { isEmpty } from "lodash";
import { createRoot } from "react-dom/client";

import { UserRole } from "../../constants/HomePage";
import { Discipline, DisciplineList, ListOptions } from "../../models/Master";
import { MyAccountModel, UserProvider } from "../../models/MyAccount";
import { ServiceProvider } from "../../models/ServiceProvider";
import { length, Logout, reloadApplication } from "../../utils";
import {
  LoginCircle1,
  LoginCircle2,
  LoginCircle3,
  LoginCircle4,
  LoginCircle5,
  Min_Vh_100,
} from "../../loginResources/backgroundStyle";
import MFA from "./mfa";
import { flexAllCentre, flexAllEnd } from "../../styles/mui/styles/display";
import {
  AddUserModalBox,
  AddUserModalGrid,
} from "../../routes/HeaderMenu/style/style";
import fontWeight from "../../styles/mui/fontWeight";
import { FaXmarkIcon } from "../../routes/CreatePriorAuth/style/style";
import { HorizontalDivider } from "../../styles/mui/styles/border";
import { ButtonContained } from "./button";
import { ButtonVerifyStyle, ErrorListStyle, SsoModalStyle } from "../styles/styles";
import { Constants } from "../../constants/Referral";
import {
  PanelBox,
  TabsPanelSectionBox,
} from "../../routes/PriorAuthWorkFlow/styles/style";
import { ResponseMessages } from "../../constants/ResponseMessage";
import { redirectUri, tangoCareUploadUrl } from "../../constants/Config";
import { msalInstance } from "../..";

export const getAgencyListByLoggedInUser = (
  masterServiceProviderDefaultList: any, //any required for multipurpose usage in this function
  loggedInUser: any,
) => {
  const agencyList =
    masterServiceProviderDefaultList &&
    masterServiceProviderDefaultList
      .filter((item: any) =>
        loggedInUser.userProviderList.some(
          (itemSelectedProvider: any) =>
            itemSelectedProvider.serviceProviderId.toString() ===
            item.serviceProviderId.toString(),
        ),
      )
      .map((item: any) => ({
        label:
          item.providerName +
          "," +
          item.providerAddress +
          "," +
          item.providerNpi,
        value: item.serviceProviderId,
      }));
  return agencyList;
};

export const getFilteredRoleType = (roleList: ListOptions[]) => {
  const roles = roleList.filter(
    (item: ListOptions) =>
      UserRole.TANGO_ADMIN !== item.label &&
      UserRole.HEALTHPLAN_USER !== item.label
  );
  return roles;
};

export const getRoleById = (id: string, masterRoleList: ListOptions[]) => {
  const role =
    masterRoleList &&
    masterRoleList.filter((item: ListOptions) => id === item.value)[0].label;
  switch (role) {
    case UserRole.TANGO_ADMIN:
      return UserRole.TANGO_ADMIN;
    case UserRole.PROVIDER_ADMIN:
      return UserRole.PROVIDER_ADMIN;
    case UserRole.PROVIDER_USER:
      return UserRole.PROVIDER_USER;
    default:
      break;
  }
};

export const getAgencyList = (
  masterServiceProviderDefaultList: ServiceProvider[],
  loggedInUser: MyAccountModel,
  isScorecard?: boolean | undefined,
) => {
  if (!length(masterServiceProviderDefaultList)) {
    return [];
  }
  const agencyList = masterServiceProviderDefaultList
    .filter((item: ServiceProvider) =>
      loggedInUser.userProviderList.some(
        (itemSelectedProvider: UserProvider) =>
          itemSelectedProvider.serviceProviderId
            ? itemSelectedProvider.serviceProviderId.toString() ===
              item.serviceProviderId.toString()
            : itemSelectedProvider.npiId.toString() ===
              item.providerNpi.toString(),
      ),
    )
    .map((item: ServiceProvider) => ({
      label: isScorecard
        ? `${item.providerName} - ${item.providerNpi}\n${item.providerAddress}`
        : item.providerName +
          "," +
          item.providerAddress +
          "," +
          item.providerNpi,
      value: item.serviceProviderId,
      npi: isScorecard ? item.providerNpi : null,
    }));

  return agencyList;
};

export const getAgencyListForTangoAdmin = (
  masterServiceProviderDefaultList: ServiceProvider[],
  isScorecard?: boolean | undefined,
) => {
  if (!length(masterServiceProviderDefaultList)) {
    return [];
  }
  const agencyList =
    masterServiceProviderDefaultList &&
    masterServiceProviderDefaultList.map((item: ServiceProvider) => ({
      label: isScorecard
        ? `${item.providerName} - ${item.providerNpi}\n${item.providerAddress}`
        : item.providerName +
          "," +
          item.providerAddress +
          "," +
          item.providerNpi,
      value: item.serviceProviderId,
      npi: isScorecard ? item.providerNpi : null,
    }));

  return agencyList;
};

export const MFAContent = ({
  handleSendMfaOnClick,
  callMfaCode,
  loggedInUser,
  handleModalClose,
  handleCloseMfaOnClick,
  userSendMfaInfoState,
  openMfaModal,
}: any) => {
  const handleSendMfa = (mfaValue: string) => handleSendMfaOnClick(mfaValue);
  const handleResendMfa = () => callMfaCode(loggedInUser, 2);
  const handleClose = (event: SyntheticEvent, reason: string) =>
    handleModalClose(event, reason);
  const handleCloseMfa = () => handleCloseMfaOnClick();

  return (
    <Box sx={Min_Vh_100}>
      <Box sx={LoginCircle1}></Box>
      <Box sx={LoginCircle2}></Box>
      <Box sx={LoginCircle3}></Box>
      <Box sx={LoginCircle4}></Box>
      <Box sx={LoginCircle5}></Box>
      <MFA
        userSendMfaInfoState={userSendMfaInfoState}
        sendMfaOnClick={handleSendMfa}
        resendMfaOnClick={handleResendMfa}
        handleModalClose={handleClose}
        closeMfaOnClick={handleCloseMfa}
        isSendButtonDisabled={false}
        isResendButtonDisabled={false}
        openModal={openMfaModal}
        isCloseButtonDisabled={false}
      />
    </Box>
  );
};

//tbd
export const getNpiList = (
  masterServiceProviderDefaultList: ServiceProvider[],
  loggedInUser: MyAccountModel,
) => {
  if (!length(masterServiceProviderDefaultList)) {
    return [];
  }
  const agencyList = masterServiceProviderDefaultList
    .filter((item: ServiceProvider) =>
      loggedInUser.userProviderList.some(
        (itemSelectedProvider: UserProvider) =>
          itemSelectedProvider.npiId.toString() === item.providerNpi.toString(),
      ),
    )
    .map((item: ServiceProvider) => ({
      label: item.providerName + ", " + item.providerNpi,
      value: item.serviceProviderId,
    }));

  return agencyList;
};

export const getNpiListForTangoAdmin = (
  masterServiceProviderDefaultList: ServiceProvider[],
) => {
  if (!length(masterServiceProviderDefaultList)) {
    return [];
  }
  const agencyList =
    masterServiceProviderDefaultList &&
    masterServiceProviderDefaultList.map((item: ServiceProvider) => ({
      label: item.providerName + ", " + item.providerNpi,
      value: item.serviceProviderId,
    }));

  return agencyList;
};

export const flattenApiValidationErrors = (error: any): string[] => {
  if (!error) return [];
  if (typeof error === "string") {
    return [error];
  }
  if (Array.isArray(error)) {
    return error.flatMap((item) => flattenApiValidationErrors(item));
  }
  if (typeof error === "object") {
    return Object.values(error).flatMap((value) => flattenApiValidationErrors(value));
  }
  return [];
};

export const showMessageModal = (
  openModal: boolean,
  setOpenModal: (value: boolean) => void,
  message: string,
  setConfirm?: (value: boolean) => void,
  isCloseHidden?: boolean,
  validationDetails?: string[]
) => {
  const detailList = validationDetails && validationDetails.length > 0 ? validationDetails : [];
  return (
    <Modal
      open={openModal}
      onClose={(_event, reason) => {
        if (reason === "backdropClick") return;
        setOpenModal(false);
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={flexAllCentre}
    >
      <Box sx={AddUserModalBox(true)}>
        <Grid container mb="0.4rem">
          <Grid size={{ xs: 11.5 }}>
            <Typography
              id="modal-modal-title"
              fontWeight={fontWeight[6]}
              variant="h3"
            >
              {message}
            </Typography>
            {detailList.length > 0 && (
              <Box sx={{ mt: 2, pl: 2 }}>
                <List disablePadding sx={ErrorListStyle}>
                  {detailList.map((detail, index) => (
                    <ListItem key={`${detail}-${index}`} disableGutters>
                      <ListItemText primary={detail} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
            {isCloseHidden ? (
              <Typography
                id="modal-title"
                fontWeight={fontWeight[6]}
                variant="h3"
                sx={{ mt: 2 }}
              >
                If you would like to submit your request while working with our
                provider relations team, you may submit your request through our{" "}
                <a
                  href={tangoCareUploadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={SsoModalStyle}
                >
                  upload center
                </a>
                .
              </Typography>
            ) : null}
          </Grid>
          <Grid size={{ xs: 0.5 }}>
            {isCloseHidden ? null : (
              <IconButton
                onClick={() => {
                  setOpenModal(false);
                  setConfirm && setConfirm(true);
                }}
                aria-label="close"
              >
                <FaXmarkIcon />
              </IconButton>
            )}
          </Grid>
        </Grid>
        <Divider sx={HorizontalDivider} />
        <Grid container sx={AddUserModalGrid}>
          <Grid size={{ xs: 12 }} sx={flexAllEnd}>
            <ButtonContained
              id="btnSubmit"
              onClick={() => {
                setOpenModal(false);
                setConfirm && setConfirm(true);
              }}
              label="Ok"
              disabled={false}
              style={ButtonVerifyStyle}
            />
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

export const checkNurseToOpenSelected = (
  listItem: number,
  disciplineListState: Discipline[],
) => {
  const selectedDiscipline =
    disciplineListState &&
    disciplineListState.filter(
      (item: Discipline) => item.disciplineName === Constants.NURSE_TO_OPEN,
    )[0];
  if (
    !isEmpty(selectedDiscipline) &&
    selectedDiscipline.disciplineId === listItem
  ) {
    return true;
  }
  return false;
};
export const getFilteredDiscipline = (discipineList: DisciplineList[]) => {
  if (discipineList !== undefined) {
    const mergedArray = [
      ...discipineList.filter((item2: DisciplineList) =>
        discipineList.some(
          (item1: DisciplineList) => item1.disciplineId === item2.disciplineId,
        ),
      ),
    ];
    const setDisciplineId = new Set<number>();
    const uniqueArray = mergedArray
      .filter((item) => {
        if (setDisciplineId.has(item.disciplineId)) return false;
        setDisciplineId.add(item.disciplineId);
        return true;
      })
      .map((item, index) => ({
        ...item,
        serviceNumber: index + 1,
        acceptedVisits: item.acceptedVisits,
      }));
    return uniqueArray;
  }
  return discipineList;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  className?: string;
}

export const TabsPanelSection = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <TabsPanelSectionBox
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <PanelBox>
          <Box>{children}</Box>
        </PanelBox>
      )}
    </TabsPanelSectionBox>
  );
};

let isReloadModalOpen = false;

export async function showReloadRequiredModal(
  status: number,
  message: string,
  header: string,
) {
  if (isReloadModalOpen) return;

  const containerId = "app-version-conflict-modal";
  let container = document.getElementById(containerId);

  if (!container) {
    container = document.createElement("div");
    container.id = containerId;
    document.body.appendChild(container);
  }

  const root = createRoot(container);

  const Modal = () => {
    const [open, setOpen] = useState<boolean>(true);

    useEffect(() => {
      isReloadModalOpen = true;
      return () => {
        isReloadModalOpen = false;
      };
    }, []);

    const handleOk = () => {
      if (status === 401) {
        localStorage.clear();
          Logout(redirectUri, msalInstance);
        return;
      }
      setOpen(false);
      root.unmount();
      isReloadModalOpen = false;
      reloadApplication();
    };

    useEffect(() => {
      if (status) {
        setOpen(true);
      }
      // eslint-disable-next-line
    }, [status]);

    return (
      <Dialog
        open={open}
        disableEscapeKeyDown
        onClose={(_event, reason) => {
          if (reason === "backdropClick") return;
        }}
      >
        <DialogTitle>{ResponseMessages(header)}</DialogTitle>
        <DialogContent>
          <DialogContentText>{ResponseMessages(message)}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <ButtonContained
            id="btnSubmit"
            onClick={() => handleOk()}
            label="Ok"
            disabled={false}
            style={ButtonVerifyStyle}
          />
        </DialogActions>
      </Dialog>
    );
  };

  root.render(createElement(Modal));
}